import { DropdownField, mergeStyles } from '@cian/components';
import * as React from 'react';

import { IExperiment } from '../../../api/experiments';
import { ButtonGroup } from '../../../common/components/button_group';
import { EBedroomsCount, ERoomType, IJsonQuery } from '../../../json_query';
import {
  dealTypeFromJsonQuery,
  FDealType,
  FOfferType,
  isSuburban,
  offerTypeFromJsonQuery,
} from '../../../utils/category';
import { formatBedRoomsLabel } from '../../../utils/format';
import { formatRoomsLabel } from '../../../utils/format_rooms_label';
import { DealType } from '../deal_type';
import { OfferType } from '../offer_type';
import { SUBURBAN_OFFER_FILTER_LABEL_MAP, SUBURBAN_OFFER_FILTER_VALUES } from '../../state/suburban_offer_filter';
import { ESuburbanOfferFilter } from '../../../repositories//common/json_query';
import { CoworkingOfferType } from '../CoworkingOfferType';
import { ECoworkingOfferType } from '../../../repositories/common/json_query/enums';

const style = require('./index.css');

export interface IDealOfferRoomsProps {
  jsonQuery: IJsonQuery;
  resultJsonQuery: IJsonQuery;
  shouldCompressFilters: boolean;
  experiments: IExperiment[];
  isAutoOfferChangeTooltipVisible: boolean;
  isCoworkingEnabled: boolean;
  isCoworkingHelperVisible: boolean;

  setDefaultSort(): void;

  closeAutoOfferChangeTooltip(): void;
  onBedroomsChanged(value: EBedroomsCount): void;
  onDealTypeChanged(value: FDealType): void;
  onRoomChanged(rooms: ERoomType[]): void;
  onOfferTypeSelected(value: FOfferType): void;
  clearRailwayTags(): void;
  onSuburbanOfferFilterChanged(value: ESuburbanOfferFilter): void;
  onCoworkingOfferTypeSelected(value: ECoworkingOfferType): void;
  onClickCoworkingHelperOutside(): void;
}

export function convertRoomsValueToNumbers(arr: React.ReactText[]): ERoomType[] {
  return arr.map(Number) as ERoomType[];
}

export class DealOfferRooms extends React.Component<IDealOfferRoomsProps> {
  public render() {
    const {
      jsonQuery,
      onBedroomsChanged,
      onSuburbanOfferFilterChanged,
      shouldCompressFilters,
      isAutoOfferChangeTooltipVisible,
      closeAutoOfferChangeTooltip,
      isCoworkingEnabled,
      isCoworkingHelperVisible,
      onClickCoworkingHelperOutside,
    } = this.props;

    const dealType = dealTypeFromJsonQuery(jsonQuery);
    const offerType = offerTypeFromJsonQuery(jsonQuery);

    const isRoomsAvailable = (offerType & FOfferType.Flat) !== 0;

    const isBedroomsVisible = dealType === FDealType.RentDaily && offerType === FOfferType.House;
    const bedroomsTotal = (jsonQuery.bedroom_total && jsonQuery.bedroom_total.value.gte) || EBedroomsCount.Any;

    const suburbanOfferFilter = jsonQuery.suburban_offer_filter
      ? jsonQuery.suburban_offer_filter.value
      : ESuburbanOfferFilter.Any;
    const isSuburbanOfferFilterVisible = isSuburban(offerType) && FDealType.Sale === dealType;
    const coworkingOffer = jsonQuery.coworking_offer_type && jsonQuery.coworking_offer_type.value;
    const isRent = (dealType & FDealType.RentLongterm) !== 0;
    const isCoworkingOfferFilterVisible = !!(isCoworkingEnabled && isRent && coworkingOffer);

    return (
      <ButtonGroup>
        <DealType jsonQuery={this.props.jsonQuery} onDealTypeChanged={this.props.onDealTypeChanged} />
        <OfferType
          jsonQuery={this.props.jsonQuery}
          onOfferTypeSelected={this.props.onOfferTypeSelected}
          setDefaultSort={this.props.setDefaultSort}
          clearRailwayTags={this.props.clearRailwayTags}
          closeAutoOfferChangeTooltip={closeAutoOfferChangeTooltip}
          isAutoOfferChangeTooltipVisible={isAutoOfferChangeTooltipVisible}
          isCoworkingEnabled={isCoworkingEnabled}
          isCoworkingHelperVisible={isCoworkingHelperVisible}
          onClickCoworkingHelperOutside={onClickCoworkingHelperOutside}
        />
        {isRoomsAvailable && (
          <DropdownField
            buttonStyle={style['button']}
            theme="button/blue"
            label={formatRoomsLabel(
              this.props.jsonQuery.room ? this.props.jsonQuery.room.value : [],
              shouldCompressFilters,
            )}
            containerStyle={style['popupContainer']}
            popupContentStyle={style['rooms-popupContent']}
            multiple
            placeholder="Комнат"
            value={this.props.jsonQuery.room ? this.props.jsonQuery.room.value.map(String) : []}
            onValueChange={value => this.props.onRoomChanged(convertRoomsValueToNumbers(value as React.ReactText[]))}
            values={[
              { value: '1', label: '1-комнатная' },
              { value: '2', label: '2-комнатная' },
              { value: '3', label: '3-комнатная' },
              { value: '4', label: '4-комнатная' },
              { value: '5', label: '5-комнатная' },
              { value: '6', label: '6-комнатная' },
              { value: '9', label: 'Студия' },
              { value: '7', label: 'Свободная планировка' },
            ]}
            dataMark="rooms"
          />
        )}
        {isBedroomsVisible && (
          <DropdownField
            buttonStyle={style['button']}
            theme="button/blue"
            label={formatBedRoomsLabel(bedroomsTotal)}
            containerStyle={style['popupContainer']}
            popupContentStyle={style['rooms-popupContent']}
            placeholder="Спален"
            value={bedroomsTotal}
            onValueChange={onBedroomsChanged}
            values={[
              { value: EBedroomsCount.Any, label: 'Неважно' },
              { value: EBedroomsCount.One, label: '1+' },
              { value: EBedroomsCount.Two, label: '2+' },
              { value: EBedroomsCount.Three, label: '3+' },
              { value: EBedroomsCount.Four, label: '4+' },
              { value: EBedroomsCount.Five, label: '5+' },
              { value: EBedroomsCount.Six, label: '6+' },
              { value: EBedroomsCount.Seven, label: '7+' },
            ]}
            dataMark="bedrooms"
          />
        )}
        {isSuburbanOfferFilterVisible && (
          <DropdownField
            // tslint:disable-next-line: no-any
            buttonStyle={mergeStyles(style['button'], style['button-suburbanOffer']) as any}
            theme="button/blue"
            label={SUBURBAN_OFFER_FILTER_LABEL_MAP[suburbanOfferFilter]}
            containerStyle={style['popupContainer']}
            popupContentStyle={style['rooms-popupContent']}
            value={suburbanOfferFilter}
            onValueChange={onSuburbanOfferFilterChanged}
            values={SUBURBAN_OFFER_FILTER_VALUES}
            dataMark="suburbanOfferFilter"
          />
        )}
        {isCoworkingOfferFilterVisible && (
          <CoworkingOfferType
            jsonQuery={this.props.jsonQuery}
            onOfferTypeSelected={this.props.onCoworkingOfferTypeSelected}
          />
        )}
      </ButtonGroup>
    );
  }
}
